import React, { Fragment, useCallback, useContext, useMemo } from 'react';

// DEPENDENCIES
import clsx from 'clsx';
import { useParams } from 'react-router-dom';
import { useFormikContext } from 'formik';

// GLOBAL FUNCTIONS
import { apiFetch, getFileType, modalFunctions } from 'functions.js';

// GLOBAL VARIABLES
import { ENDPOINTS } from 'endpoints';

// GLOBAL HELPERS/FUNCTIONS
import { RecordContext } from 'helpers/getRecordData';
import useRecordView from 'helpers/getRecordView';

// CORE COMPONENTS
import Button from 'core/tools/Button';
import Form from 'core/form/Form';
import Number from 'core/tools/Number';

// LOCAL COMPONENTS
import AddRepairInvoice from './AddRepairInvoice';
import InputAppendCol from './InputAppendCol';
import UploadItem from '../../tools/UploadItem';

// STYLES
import styles from './repairInvoice.module.scss';

// MAIN COMPONENT
const RepairInvoice = () => {

  // CONTEXT
  const { recordID } = useParams();
  const { values, setFieldValue } = useFormikContext() || {};
  const { record: { inventory, ...record } = {}, refetchRecord } = useContext(RecordContext) || {};
  const { isEdit, isInventory, isView } = useRecordView();

  // MEMOS
  const {
    daily_sales_inventory_id,
    aryRepairInvoice = []
  } = useMemo(
    () => !isInventory ? inventory || record : isEdit ? values : record,
    [record, inventory, values, isEdit, isInventory]
  );

  const dailySalesInventoryId = useMemo(
    () => isInventory ?  recordID : daily_sales_inventory_id,
    [recordID, daily_sales_inventory_id, isInventory]
  );

  // CALLBACKS
  const handleDelete = useCallback(
    inventory_repair_invoice_id => {
      modalFunctions.add({
        type: 'confirmation',
        title: 'Delete Repair Invoice',
        body: 'Are you sure you want to delete this Repair Invoice?',
        closeButton: false,
        continueButton: {
          label: 'Delete',
          variant: 'danger',
          icon: 'trash',
          onClick: () => apiFetch({
            method: 'DELETE',
            endpoint: ENDPOINTS.inventory.repair.delete(dailySalesInventoryId, inventory_repair_invoice_id),
            onSuccess: () => isView ? refetchRecord() : refetchRecord({
              onSuccess: ({ result }) => {
                const { aryRepairInvoice } = result;
                setFieldValue('aryRepairInvoice', aryRepairInvoice)
              }
            }),
            successMessage: 'Repair invoice removed successfully',
            errorMessage: 'Unable to remove repair invoice.',
          })
        },
        spacing: 'sm'
      })
    },
    [dailySalesInventoryId, setFieldValue, refetchRecord, isView]
  )

  // RENDER
  return (<>
    {aryRepairInvoice.map((v, i) => (
      <Fragment key={i}>
        <Form.PlainText
          label={v.vendor_name}
          value={<>
            <Number
              value={v.invoice_amount}
              schema="dollars"
            />
            {isInventory &&
              <Button.Link
                className={clsx('ms-3', styles.deleteButton)}
                icon="trash"
                onClick={() => handleDelete(v.inventory_repair_invoice_id)}
              />
            }
          </>}
          input={{
            append: <InputAppendCol cols={{ sm: 16 }}>{v.comments}</InputAppendCol>
          }}
        />
        {v.documentAttachment.map((attachment, i) => (
          <Form.Col key={i} cols={{ xs: 24 }} className="mt-n4">
            <Form.Row>
              <Form.Col cols={{ xs: 24, className: clsx('py-0', styles.label) }} />
              <Form.Col>
                <UploadItem
                  meta={{
                    fileName: attachment.inventoryattachment_filename,
                    uploadedBy: attachment.updated_by || attachment.created_by,
                    uploadedOn: attachment.date_updated || attachment.date_created
                  }}
                  viewArgs={{
                    endpoint: ENDPOINTS.inventory.repair.viewAttachment(
                      dailySalesInventoryId,
                      v.inventory_repair_invoice_id,
                      attachment.inventoryattachment_id
                    ),
                    mimeType: getFileType(attachment.inventoryattachment_filename) || 'text/plain'
                  }}
                  allowDelete={false}
                  className="p-0"
                  containerClassName={styles.attachment}
                />
              </Form.Col>
            </Form.Row>
          </Form.Col>
        ))}
      </Fragment>
    ))}
    {isInventory &&
      <AddRepairInvoice />
    }
  </>);
};

// EXPORT
export default RepairInvoice;
